/**
 * The one control that reunites a previewed file with the conversation it came from (owner directive
 * 2026-08-24, round 2): "open chat", which brings Prest0n up as the docked right rail while the preview keeps
 * the rest of the screen (FilePreviewOverlay's companion layout does the reflow; App's RoutedContentShell
 * moves the page under both).
 *
 * ONE component for BOTH of today's shells (modal + floating window) on purpose: the copy and the tooltip can
 * never drift between them. It only draws: where a file came from (PreviewOriginContext) and what the control
 * does are src/hooks/filePreview/previewOrigin.ts (PV5-8:C part 1, 2026-10-13), which the new design's shells
 * draw from too.
 */
import React from 'react';
import { ToolButton } from './PreviewContent';
import { chatBesideControl, type PreviewOriginContext } from '../../hooks/filePreview/previewOrigin';

export interface OpenChatBesideButtonProps {
  origin?: PreviewOriginContext | null;
  /** Fired after a visible chat claims the request (shells re-place themselves). */
  onOpened?: () => void;
}

/**
 * The control itself. Wears the shells' shared ToolButton chrome, so it reads
 * as one of the preview's own tools rather than a bolted-on affordance.
 */
export const OpenChatBesideButton: React.FC<OpenChatBesideButtonProps> = ({ origin, onOpened }) => {
  const c = chatBesideControl(origin, onOpened);
  return (
    <ToolButton
      data-preview-open-chat=""
      aria-label={c.hasThread
        ? 'open the conversation this file came from, beside this preview'
        : 'open the chat beside this preview'}
      title={c.hasThread
        ? 'Open the conversation this file came from — Prest0n docks on the right, this preview keeps the rest of the screen'
        : 'Open Prest0n on the right and keep this preview beside it'}
      onPointerDown={c.onPointerDown}
      onClick={c.onClick}
    >
      Open chat
    </ToolButton>
  );
};

export default OpenChatBesideButton;
